import {
  cents,
  totalOutstanding,
  type Balance,
  PRODUCTION_COLUMNS,
  PRODUCTION_COLUMN_LABEL,
  isLost,
  jobAddress,
  jobMatches,
  outstandingSteps,
  productionColumn,
  type Job,
  type JobChecklistItem,
  type ProductionColumn,
} from "@roof-hawk/core";
import { useMemo } from "react";

import { ListHead } from "../../components/ui";
import { t, tn } from "../../i18n";
import { navigate, useAsync } from "../../lib/hooks";
import { fetchProductionBoard } from "../../lib/queries";
import { boardBalanceFor } from "./boardBalance";
import { DepositsPanel } from "./DepositsPanel";
import type { AppData } from "../shell/Shell";

/**
 * Ongoing Jobs — the six columns an approved job walks through.
 *
 * Nothing on this board is dragged. Every column is decided by the approved
 * checklist the team already ticks, so a job moves because the work moved. The
 * columns are named for what is OUTSTANDING rather than what has happened,
 * because the board's job is to say what to do today.
 *
 * The rule itself is `productionColumn` in packages/core, which is where it can
 * be tested and where the MCP server reads it from too.
 */
export function ProductionBoard({
  data,
  search,
}: {
  data: AppData;
  search: string;
}) {
  // Approved and not written off. Closed Won stays on the board so the last
  // column is not permanently empty; it is the receipt for the week.
  const jobs = useMemo(
    () =>
      data.jobs
        .filter((job) => Boolean(job.sends_at) && !isLost(job))
        .filter((job) => jobMatches(job, search)),
    [data.jobs, search],
  );

  const jobIds = useMemo(() => jobs.map((j) => j.id), [jobs]);
  const board = useAsync(
    () => fetchProductionBoard(jobIds),
    [jobIds.join(",")],
  );

  const itemsByJob = useMemo(() => {
    const map = new Map<string, JobChecklistItem[]>();
    for (const item of board.data?.items ?? []) {
      const list = map.get(item.job_id);
      if (list) list.push(item);
      else map.set(item.job_id, [item]);
    }
    return map;
  }, [board.data]);

  /**
   * What each customer still owes.
   *
   * Jonathon, 2026-09-06: "on the overview page there should be a remaining
   * balance on approved or (sent jobs) so we can keep up with who owes us money
   * and how much." This board is exactly those jobs — approved, not written off
   * — so it is where the figure belongs.
   *
   * The contract comes from `data.values`, which the shell has already worked
   * out for every job on screen. What counts as paid is money in the Mercury
   * account and nothing else — his instruction, overruling a first version that
   * read the checklist: "what counts as paid should be when that specific money
   * hits our bank account in mercury." `jobBalance` in packages/core does the
   * arithmetic (CLAUDE.md rule 5) and the note there says what that misses.
   */
  const placed = useMemo(() => {
    const sizes = board.data?.sizes ?? {};
    return jobs.map((job) => {
      const items = itemsByJob.get(job.id) ?? [];
      return {
        job,
        column: productionColumn({
          contractCents: sizes[job.id] ?? 0,
          items,
        }),
        items,
        balance: boardBalanceFor(
          board.data,
          job.id,
          data.values?.get(job.id) ?? cents(0),
        ),
      };
    });
  }, [jobs, itemsByJob, board.data, data.values]);

  if (board.loading && !board.data) {
    return (
      <div className="list">
        <div className="empty">
          <b>{t("Loading the board…")}</b>
        </div>
      </div>
    );
  }

  const waitingOnMoney = placed.filter(
    (p) =>
      p.column === "needs_first_payment" || p.column === "needs_final_payment",
  ).length;

  const owed = totalOutstanding(placed.map((p) => p.balance));

  return (
    <>
      <ListHead title={t("Ongoing Jobs")} count={jobs.length}>
        {owed.outstanding > 0 && (
          <span className="owed" role="status">
            {tn(
              owed.jobs,
              "{amount} owed across 1 job",
              "{amount} owed across {n} jobs",
              { amount: owed.toCollectFormatted },
            )}
            {owed.onItsWay > 0 &&
              t(", {amount} on its way", { amount: owed.onItsWayFormatted })}
          </span>
        )}
        {waitingOnMoney > 0 && (
          <span className="needscall" role="status">
            {t("{n} waiting on payment", { n: waitingOnMoney })}
          </span>
        )}
      </ListHead>

      <DepositsPanel />

      <div className="prodboard">
        {PRODUCTION_COLUMNS.map((column) => {
          const inColumn = placed.filter((p) => p.column === column.key);
          return (
            <div className={`pcol tone-${column.tone}`} key={column.key}>
              <h3>
                {t(PRODUCTION_COLUMN_LABEL[column.key])}{" "}
                <span className="c">{inColumn.length}</span>
              </h3>
              {inColumn.length === 0 ? (
                <div className="none">{t("None")}</div>
              ) : (
                inColumn.map(({ job, column: key, items, balance }) => (
                  <ProductionCard
                    key={job.id}
                    job={job}
                    column={key}
                    items={items}
                    balance={balance}
                  />
                ))
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function ProductionCard({
  job,
  column,
  items,
  balance,
}: {
  job: Job;
  column: ProductionColumn;
  items: readonly JobChecklistItem[];
  balance: Balance;
}) {
  // What is actually holding this job up, in the column's own terms. Two is
  // enough to act on; the rest is on the job page.
  const waiting = outstandingSteps({ contractCents: 0, items }, column);

  return (
    <div
      className="pcard replacement"
      onClick={() => navigate(`job=${job.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") navigate(`job=${job.id}`);
      }}
    >
      <div className="cn">{job.contact?.full_name ?? "—"}</div>
      <div className="ca">{jobAddress(job)}</div>
      <div className="pstage">
        #{job.job_number}
        {job.rep?.full_name ? ` · ${job.rep.full_name}` : ""}
      </div>
      {balance.owing && (
        <div className="powed">
          {t("Owes {amount}", { amount: balance.toCollectFormatted })}
          {balance.onItsWay > 0 && (
            <span className="pway">
              {" "}
              {t("+ {amount} on its way", {
                amount: balance.onItsWayFormatted,
              })}
            </span>
          )}
        </div>
      )}
      {waiting.length > 0 && (
        <div className="waiting">
          {waiting
            .slice(0, 2)
            .map((step) => t(step))
            .join(", ")}
          {waiting.length > 2
            ? ` ${t("+{n} more", { n: waiting.length - 2 })}`
            : ""}
        </div>
      )}
    </div>
  );
}
